import React, { useState, useEffect } from 'react';
import { Download, Filter, FileSpreadsheet, Calendar, Store, ArrowRight } from 'lucide-react';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
// Motor do lote SAP: mora fora do componente por ser lógica de dinheiro testada
// (ver pettycash-sap-utils.test.js).
import { gerarLinhasSAP, mesDoLancamento, valorDoLancamento } from './pettycash-sap-utils';

const FinanceiroPettyCashRelatorio = () => {
    const [transacoes, setTransacoes] = useState([]);
    const [lojas, setLojas] = useState([]);
    const [isCarregando, setIsCarregando] = useState(true);
    
    // Estados dos Filtros
    const [mesAnoSelecionado, setMesAnoSelecionado] = useState('');
    const [lojaSelecionada, setLojaSelecionada] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken'); 
        
        // Busca Lojas
        fetch('/api/pettycash/lojas', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => { if (Array.isArray(data)) setLojas(data); })
        .catch(err => console.error("Erro ao carregar lojas", err));

        // Busca Transações — com incluirAjustes=1, porque aqui a falta/sobra
        // (TIPO='AJUSTE') é movimento próprio do lote contábil, não detalhe do pai.
        fetch('/api/pettycash/listar?incluirAjustes=1', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => {
            if (Array.isArray(data)) {
                // Apenas as aprovadas vão para o SAP e para os gráficos
                const aprovadas = data.filter(t => t.STATUS === 'Aprovado');
                setTransacoes(aprovadas);
            }
        })
        .catch(err => console.error("Erro ao carregar dados do relatório", err))
        .finally(() => setIsCarregando(false));
    }, []);

    // ========================================================================
    // PROCESSAMENTO DE FILTROS E GRÁFICOS
    // ========================================================================
    // `mesDoLancamento` e `valorDoLancamento` vêm do módulo do lote SAP: gráficos,
    // filtros e lote precisam enxergar o mesmo valor/competência (o do CAIXA).
    const periodosDisponiveis = Array.from(new Set(transacoes.map(mesDoLancamento))).sort().reverse();

    const transacoesFiltradas = transacoes.filter(t => {
        const passaMes = mesAnoSelecionado ? mesDoLancamento(t) === mesAnoSelecionado : true;
        const passaLoja = lojaSelecionada ? t.LOJA === lojaSelecionada : true;
        return passaMes && passaLoja;
    });

    // Gráfico 1: Evolução Geral (Linha)
    const dadosEvolucao = transacoesFiltradas.reduce((acc, curr) => {
        const mesAno = mesDoLancamento(curr);
        const existente = acc.find(item => item.periodo === mesAno);
        if (existente) {
            existente.valor += valorDoLancamento(curr);
        } else {
            acc.push({ periodo: mesAno, valor: valorDoLancamento(curr) });
        }
        return acc;
    }, []).reverse();

    // Gráfico 2: Despesas por Loja (Barra)
    const dadosPorLoja = transacoesFiltradas.reduce((acc, curr) => {
        const existente = acc.find(item => item.loja === curr.LOJA);
        if (existente) {
            existente.valor += valorDoLancamento(curr);
        } else {
            acc.push({ loja: curr.LOJA, valor: valorDoLancamento(curr) });
        }
        return acc;
    }, []).sort((a, b) => b.valor - a.valor);

    // ========================================================================
    // TABELA PADRÃO SAP — montagem em `pettycash-sap-utils.js` (testada)
    // ========================================================================
    const linhasTabelaSAP = gerarLinhasSAP(transacoesFiltradas);

    const salvarLoteSAPNoBanco = async () => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');

        const payload = {
            periodo: mesAnoSelecionado || 'CONSOLIDADO_HISTORICO',
            loja: lojaSelecionada || 'TODAS',
            linhas: linhasTabelaSAP
        };

        const response = await fetch('/api/pettycash/sap-lote/salvar', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Erro ao salvar lote SAP no banco.');
        }

        return data;
    };

    // ========================================================================
    // EXPORTAÇÃO PARA O SAP (CSV)
    // ========================================================================
    const exportarParaSAP = async () => {
        if (linhasTabelaSAP.length === 0) {
            alert("Não há dados aprovados para exportar neste período.");
            return;
        }

        try {
            const resultadoSalvamento = await salvarLoteSAPNoBanco();

            if (resultadoSalvamento.duplicado) {
                const continuar = window.confirm(
                    `Este lote já estava salvo no banco com o ID ${resultadoSalvamento.loteId}.\n\nDeseja baixar o CSV mesmo assim?`
                );

                if (!continuar) return;
            } else {
                alert(`Lote SAP salvo com sucesso! ID do lote: ${resultadoSalvamento.loteId}`);
            }

            const cabecalho = [
                '#',
                'Cta.contáb./cód.PN',
                'Cta.cont./Nome PN',
                'Débito',
                'Crédito',
                'Observações',
                'Regra distr.',
                'Filial'
            ];
            
            const linhasCSV = linhasTabelaSAP.map(r => {
                return [
                    r.linha,
                    r.conta,
                    r.nomeConta,
                    r.debito !== null ? r.debito.toFixed(2).replace('.', ',') : '',
                    r.credito !== null ? r.credito.toFixed(2).replace('.', ',') : '',
                    r.observacoes,
                    r.regraDistr,
                    r.filial
                ].join(';'); 
            });

            const csvContent = [cabecalho.join(';'), ...linhasCSV].join('\n');
            
            const blob = new Blob(["\uFEFF" + csvContent], {
                type: 'text/csv;charset=utf-8;'
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.setAttribute("href", url);
            link.setAttribute(
                "download",
                `Exportacao_SAP_PettyCash_${lojaSelecionada || 'Geral'}_${mesAnoSelecionado ? mesAnoSelecionado.replace('/', '-') : 'Geral'}.csv`
            );

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Erro ao salvar/exportar lote SAP:', error);
            alert(error.message || 'Erro ao salvar/exportar lote SAP.');
        }
    };

    const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', width: '220px' };

    return (
        <div>
            {/* CABEÇALHO E FILTROS */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', backgroundColor: '#fff', padding: '16px 24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                        <Filter size={18} /> <span style={{ fontWeight: '600', fontSize: '14px' }}>Filtros de Relatório:</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                        <Store size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                        <select value={lojaSelecionada} onChange={(e) => setLojaSelecionada(e.target.value)} style={{...inputStyle, paddingLeft: '36px'}}>
                            <option value="">Todas as Lojas</option>
                            {lojas?.map((l, i) => <option key={i} value={l.NOME_LOJA}>{l.NOME_LOJA}</option>)}
                        </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                        <Calendar size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                        <select value={mesAnoSelecionado} onChange={(e) => setMesAnoSelecionado(e.target.value)} style={{...inputStyle, paddingLeft: '36px'}}>
                            <option value="">Consolidado Histórico</option>
                            {periodosDisponiveis.map((periodo, i) => <option key={i} value={periodo}>{periodo}</option>)}
                        </select>
                    </div>
                </div>

                <button onClick={exportarParaSAP} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', border: 'none', borderRadius: '8px', backgroundColor: '#16a39c', color: '#fff', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 6px rgba(22, 163, 156, 0.2)', marginTop: window.innerWidth <= 768 ? '16px' : '0' }}>
                    <Download size={18} /> Salvar e Exportar Lote SAP
                </button>
            </div>

            {isCarregando ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Carregando dados financeiros...</div>
            ) : (
                <>
                    {/* SESSÃO DE GRÁFICOS */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                        <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0f172a' }}>Evolução de Gastos (Geral)</h3>
                            <div style={{ height: '300px', width: '100%' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={dadosEvolucao}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="periodo" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(value) => `R$ ${value}`} />
                                        <Tooltip formatter={(value) => [`R$ ${value.toFixed(2)}`, 'Total']} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                                        <Line type="monotone" dataKey="valor" stroke="#16a39c" strokeWidth={3} dot={{ r: 4, fill: '#16a39c', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0f172a' }}>Consumo por Loja {mesAnoSelecionado && `(${mesAnoSelecionado})`}</h3>
                            <div style={{ height: '300px', width: '100%' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dadosPorLoja} layout="vertical" margin={{ left: 40 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                        <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                        <YAxis dataKey="loja" type="category" tick={{ fontSize: 12, fill: '#0f172a', fontWeight: 600 }} axisLine={false} tickLine={false} width={120} />
                                        <Tooltip formatter={(value) => [`R$ ${value.toFixed(2)}`, 'Gasto']} cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                                        <Bar dataKey="valor" fill="#2dd4bf" radius={[0, 4, 4, 0]} barSize={24} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* SESSÃO: TABELA MODELO SAP */}
                    <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FileSpreadsheet size={20} color="#16a39c" />
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>Visualização de Lote Contábil SAP</h3>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '950px' }}>
                                <thead style={{ backgroundColor: '#f8fafc', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                                    <tr>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', width: '40px' }}>#</th>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>Cta.contáb./cód.PN</th>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>Cta.cont./Nome PN</th>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>Débito</th>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>Crédito</th>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>Observações</th>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>Regra distr.</th>
                                        <th style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>Filial</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhasTabelaSAP.length === 0 ? (
                                        <tr><td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Sem lançamentos aprovados para os filtros atuais.</td></tr>
                                    ) : (
                                        linhasTabelaSAP.map((r) => (
                                            <tr key={r.linha} style={{ 
                                                borderBottom: '1px solid #e2e8f0', 
                                                fontSize: '13px', 
                                                color: '#475569',
                                                backgroundColor: r.credito ? '#f8fafc' : '#ffffff' // Destaca a linha de Crédito levemente
                                            }}>
                                                <td style={{ padding: '12px 16px', color: '#94a3b8' }}>{r.linha}</td>
                                                <td style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <ArrowRight size={14} color="#f59e0b" /> {r.conta}
                                                </td>
                                                <td style={{ padding: '12px 16px', fontWeight: r.credito ? '600' : '400', color: '#0f172a' }}>{r.nomeConta}</td>
                                                <td style={{ padding: '12px 16px' }}>{r.debito !== null ? `R$ ${r.debito.toFixed(2).replace('.', ',')}` : ''}</td>
                                                <td style={{ padding: '12px 16px' }}>{r.credito !== null ? `R$ ${r.credito.toFixed(2).replace('.', ',')}` : ''}</td>
                                                <td style={{ padding: '12px 16px' }}>{r.observacoes}</td>
                                                <td style={{ padding: '12px 16px', display: r.regraDistr ? 'flex' : 'table-cell', alignItems: 'center', gap: '6px' }}>
                                                    {r.regraDistr && <ArrowRight size={14} color="#f59e0b" />} {r.regraDistr}
                                                </td>
                                                <td style={{ padding: '12px 16px' }}>{r.filial}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FinanceiroPettyCashRelatorio;